/**
 * Engine output → contract shapes.
 */
import type {
  SeriesKey,
  TimeSeries,
  WidgetData,
  WidgetId,
  WidgetLoader,
} from '@/data-sources/contract';
import type { VoiceOrigin } from '@/data-sources/contract/widgets/brand-health';
import { isCommentOrReply } from '@/lib/analytics/conversations';
import { toIsoDate } from '@/lib/analytics/dates';

import { aggregate, type AggregateResult, type AggregateSpec } from '../engine/aggregate';
import type { MentionRow } from '../engine/generator';
import type { Bounds } from '../query';
import { bucketsIn } from '../engine/buckets';
import type { DimensionKey } from '../engine/dimensions';

/**
 * An `aggregate()` result as a contract `TimeSeries`. The engine's rows carry
 * a display label per bucket; the contract carries the bucket start instead and
 * the presenter re-labels it — the same `bucketLabel`, so the axis is identical.
 */
export function timeSeriesOf(
  result: AggregateResult,
  from: number,
  to: number,
  splitBy?: DimensionKey,
  /** The comparison period, aggregated on `result.bucket` — see `comparisonOf`. */
  previous?: AggregateResult | null,
): TimeSeries {
  const starts = bucketsIn(from, to, result.bucket);
  return {
    ...(previous !== undefined && {
      previous: previous && previousSeriesOf(result, previous, splitBy),
    }),
    bucket: result.bucket,
    x: starts.map((start) => toIsoDate(start)),
    series: result.series.map((s) => {
      const key: SeriesKey = splitBy ? { key: s.key, dimension: splitBy } : { key: s.key };
      return { ...key, values: result.rows.map((row) => Number(row[s.key] ?? 0)) };
    }),
  };
}

/**
 * The comparison period aggregated **on the current period's bucket**, or
 * `null` when comparison is off. Left to size its own bucket, a comparison
 * range of a different length could come out weekly beside a daily chart, and
 * the nth bucket would no longer mean the same span.
 */
export function comparisonOf(
  rows: readonly MentionRow[] | null,
  bounds: Bounds | null,
  current: AggregateResult,
  spec: Omit<AggregateSpec, 'from' | 'to' | 'bucket'>,
): AggregateResult | null {
  if (!rows || !bounds) return null;
  return aggregate(rows, { ...spec, from: bounds.from, to: bounds.to, bucket: current.bucket });
}

/**
 * One previous-values entry per *current* series, aligned by bucket ordinal —
 * the semantics of `mergeComparison`. A series the comparison period never had
 * reads 0 at each bucket it does have; a bucket it does not have reads `null`.
 */
function previousSeriesOf(
  current: AggregateResult,
  previous: AggregateResult,
  splitBy?: DimensionKey,
): NonNullable<TimeSeries['previous']> {
  return current.series.map((s) => {
    const key: SeriesKey = splitBy ? { key: s.key, dimension: splitBy } : { key: s.key };
    return {
      ...key,
      values: current.rows.map((_, i) => {
        const prior = previous.rows[i];
        return prior ? Number(prior[s.key] ?? 0) : null;
      }),
    };
  });
}

/**
 * A categorical widget's loader, with the same payload over the comparison
 * range beside it as `previous` — the loader itself, run again on the
 * comparison range with comparison off. One implementation of the widget's
 * math, so a bar's "before" can never be computed differently from its "now".
 */
export function withComparison<W extends WidgetId>(load: WidgetLoader<W>): WidgetLoader<W> {
  return async (request, ctx) => {
    const current = await load(request, ctx);
    const { compare } = request.query;
    if (!compare) return { ...current, previous: null } as WidgetData<W>;
    const previous = await load(
      { ...request, query: { ...request.query, range: compare, compare: null } },
      ctx,
    );
    return { ...current, previous } as WidgetData<W>;
  };
}

/**
 * What the sample's rows stand in for on our official pages: it has no pages
 * and no post-to-comment links, so its placed (seeded) rows stand in for our
 * pages' posts (`top-performing-content`'s `official`) and the comments and
 * replies among them for what people said under those posts. Disjoint from the
 * organic rows that stand in for the community's.
 */
export const isOfficialPostMention = (row: MentionRow) =>
  row.origin === 'seeded' && isCommentOrReply(row.conversation);

/** Our rows for one `VoiceOrigin` — see `isOfficialPostMention`. */
export function voiceRows(
  rows: readonly MentionRow[],
  origin: VoiceOrigin | undefined,
): readonly MentionRow[] {
  if (origin === 'official_post_mentions') return rows.filter(isOfficialPostMention);
  if (origin === 'community') return rows.filter((row) => row.origin === 'organic');
  return rows;
}
